import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useApolloClient, useMutation, type DocumentNode } from "@apollo/client";
import { ContextApp } from "@config/Context/contextApp";
import {
  setApprovalFlowAbilitiesState,
  useAssignmentProposalStore,
} from "@context/useAssignmentProposalStore";
import { useSettingsStore } from "@context/useSettingsStore";
import type {
  AssignmentProposal,
  AssignmentProposalErrorCode,
  AssignmentProposalResult,
  CubeMutation,
  CubeMutationApplyAssignmentProposalArgs,
  CubeMutationPreApproveAssignmentProposalArgs,
  CubeMutationRejectAssignmentProposalArgs,
  CubeMutationSubmitAssignmentProposalArgs,
  Mutation,
  Query,
} from "@models/ISchema";
import type {
  ProposalDecisionOutcome,
  ProposalSubmitOutcome,
  ReviewerOption,
} from "@services/types/assignment-proposal/assignmentProposalTypes";
import {
  canReviewProposals,
  canSubmitProposals,
  hasOpenedProposal,
  isProposalMode,
} from "@utils/assignmentProposal";
import {
  buildReviewerFilter,
  getRejectionReason,
  normalizeDecisionResult,
  normalizeSubmitResult,
  toReviewerOption,
  withoutUser,
} from "./assignmentProposalAdapters";
import approvalFlowAbilitiesQuery from "./graphql/approvalFlowAbilities.query.graphql";
import assignmentProposalQuery from "./graphql/assignmentProposal.query.graphql";
import assignmentProposalForReviewQuery from "./graphql/assignmentProposalForReview.query.graphql";
import applyAssignmentProposalMutation from "./graphql/applyAssignmentProposal.mutation.graphql";
import preApproveAssignmentProposalMutation from "./graphql/preApproveAssignmentProposal.mutation.graphql";
import rejectAssignmentProposalMutation from "./graphql/rejectAssignmentProposal.mutation.graphql";
import reviewerOptionsQuery from "./graphql/reviewerOptions.query.graphql";
import submitAssignmentProposalMutation from "./graphql/submitAssignmentProposal.mutation.graphql";

/**
 * Loads the approval-flow permissions of the logged user into the proposal store, once per page that
 * needs them, only with the flow on, and again on every retry. Not part of the login query, so a
 * backend without these fields cannot break the login; no-cache keeps this partial abilities object
 * out of the normalized cache the login query wrote.
 */
export const useLoadApprovalFlowAbilities = () => {
  const client = useApolloClient();
  const isApprovalFlowEnabled = useSettingsStore(state => state.isApprovalFlowEnabled);
  const userId = useContext(ContextApp)?.user?.id;
  const attempt = useAssignmentProposalStore(state => state.abilitiesAttempt);

  useEffect(() => {
    if (!isApprovalFlowEnabled || !userId) {
      setApprovalFlowAbilitiesState(null, "idle");
      return undefined;
    }

    let isActive = true;
    setApprovalFlowAbilitiesState(null, "loading");

    // The app-wide default for client.query is errorPolicy "all", which resolves GraphQL errors
    // instead of rejecting; "none" makes a failing or unsupported query reach the catch below.
    client
      .query<Query>({
        query: approvalFlowAbilitiesQuery,
        variables: { userId },
        fetchPolicy: "no-cache",
        errorPolicy: "none",
      })
      .then(({ data }) => {
        if (!isActive) return;
        setApprovalFlowAbilitiesState(data?.base?.user?.abilities ?? null, "loaded");
      })
      .catch(error => {
        if (!isActive) return;
        // Fail closed: the mode stays unknown, so nothing is saved, until a retry loads them.
        console.error("[ApprovalFlowAbilities]", error);
        setApprovalFlowAbilitiesState(null, "error");
      });

    return () => {
      isActive = false;
    };
  }, [client, isApprovalFlowEnabled, userId, attempt]);

  // Leaving the page drops the answer, so the next visit starts unresolved (fail closed) instead
  // of rendering once with the abilities of the previous visit.
  useEffect(() => () => setApprovalFlowAbilitiesState(null, "idle"), []);
};

/** The approval-flow setting and permissions, and whether they are still unknown. */
const useApprovalFlowAccess = () => {
  const isApprovalFlowEnabled = useSettingsStore(state => state.isApprovalFlowEnabled);
  const areSettingsLoaded = useSettingsStore(state => state.areSettingsLoaded);
  const abilities = useAssignmentProposalStore(state => state.abilities);
  const abilitiesStatus = useAssignmentProposalStore(state => state.abilitiesStatus);

  // Unknown until the settings arrive, and, when the flow is on, until the abilities arrive. A
  // failed abilities request keeps them unknown: the flow is a control, so it fails closed.
  const isResolving = !areSettingsLoaded || (isApprovalFlowEnabled && abilitiesStatus !== "loaded");
  const hasAbilitiesError = isApprovalFlowEnabled && abilitiesStatus === "error";

  return { isApprovalFlowEnabled, abilities, isResolving, hasAbilitiesError };
};

/**
 * The single place that decides whether the editor runs in proposal mode. While the flow is on and
 * the permissions have not arrived, or their request failed (`hasAbilitiesError`), the mode is
 * unknown (isResolving): consumers must keep the validate and save-draft actions disabled so a
 * requester cannot reach a direct save.
 *
 * An editor opened for an existing proposal (one of the user's, or one under review) runs in
 * proposal mode for anyone with a role in the flow, also a user who can incorporate and would
 * otherwise save directly: the content of a proposal must never be one click away from Darwin's
 * schedule.
 */
export const useProposalMode = () => {
  const { isApprovalFlowEnabled, abilities, isResolving, hasAbilitiesError } =
    useApprovalFlowAccess();
  const isProposalOpened = useAssignmentProposalStore(hasOpenedProposal);
  const access = { isApprovalFlowEnabled, abilities };
  const hasRole = canSubmitProposals(access) || canReviewProposals(access);

  return {
    isProposalMode: !isResolving && (isProposalMode(access) || (isProposalOpened && hasRole)),
    isResolving,
    hasAbilitiesError,
  };
};

/**
 * The roles of the logged user in the flow: requester (`canSubmit`: flow on, umbrella and send)
 * and reviewer (`canReview`: flow on, umbrella and intermediate approval or incorporate). They show
 * "Ver solicitudes de cambio", pick the Drawer lists and let the editor open a proposal. Unknown
 * while the settings or the permissions are loading, and after a failed permissions request.
 */
export const useApprovalFlowRoles = () => {
  const { isApprovalFlowEnabled, abilities, isResolving, hasAbilitiesError } =
    useApprovalFlowAccess();
  const access = { isApprovalFlowEnabled, abilities };

  return {
    canSubmit: !isResolving && canSubmitProposals(access),
    canReview: !isResolving && canReviewProposals(access),
    isResolving,
    hasAbilitiesError,
  };
};

export type CubeAnswer = CubeMutation | null | undefined;

/** Variables of a mutation under `cube`: its scenario and origin, plus the field's own arguments. */
type CubeVariables<TArgs> = TArgs & { scenarioId: string; originId: string };

/**
 * A proposal mutation that resolves to a discriminated outcome. A call made while another one is in
 * flight resolves to null, and a request that throws reads as no answer.
 */
export const useProposalMutation = <TVariables extends object, TOutcome>(
  document: DocumentNode,
  name: string,
  normalize: (cube: CubeAnswer) => TOutcome,
) => {
  const [mutate] = useMutation<Mutation>(document);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isInFlight = useRef(false);

  const submit = useCallback(
    async (variables: TVariables): Promise<TOutcome | null> => {
      if (isInFlight.current) return null;

      isInFlight.current = true;
      setIsSubmitting(true);

      try {
        const { data } = await mutate({ variables });
        return normalize(data?.cube);
      } catch (error) {
        console.error(`[${name}]`, error);
        return normalize(null);
      } finally {
        isInFlight.current = false;
        setIsSubmitting(false);
      }
    },
    [mutate, name, normalize],
  );

  return { submit, isSubmitting };
};

const normalizeSubmitAnswer = (cube: CubeAnswer) =>
  normalizeSubmitResult(cube?.submitAssignmentProposal);
const normalizePreApprovalAnswer = (cube: CubeAnswer) =>
  normalizeDecisionResult(cube?.preApproveAssignmentProposal);
const normalizeApplicationAnswer = (cube: CubeAnswer) =>
  normalizeDecisionResult(cube?.applyAssignmentProposal);
const normalizeRejectionAnswer = (cube: CubeAnswer) =>
  normalizeDecisionResult(cube?.rejectAssignmentProposal);

/** Saves a draft or sends the proposal. */
export const useSubmitAssignmentProposal = () =>
  useProposalMutation<
    CubeVariables<CubeMutationSubmitAssignmentProposalArgs>,
    ProposalSubmitOutcome
  >(submitAssignmentProposalMutation, "SubmitAssignmentProposal", normalizeSubmitAnswer);

/** The intermediate approval. */
export const usePreApproveAssignmentProposal = () =>
  useProposalMutation<
    CubeVariables<CubeMutationPreApproveAssignmentProposalArgs>,
    ProposalDecisionOutcome
  >(
    preApproveAssignmentProposalMutation,
    "PreApproveAssignmentProposal",
    normalizePreApprovalAnswer,
  );

/** The final approval: the only decision that changes Darwin's schedule. */
export const useApplyAssignmentProposal = () =>
  useProposalMutation<
    CubeVariables<CubeMutationApplyAssignmentProposalArgs>,
    ProposalDecisionOutcome
  >(applyAssignmentProposalMutation, "ApplyAssignmentProposal", normalizeApplicationAnswer);

/** The rejection with its reason. */
export const useRejectAssignmentProposal = () =>
  useProposalMutation<
    CubeVariables<CubeMutationRejectAssignmentProposalArgs>,
    ProposalDecisionOutcome
  >(rejectAssignmentProposalMutation, "RejectAssignmentProposal", normalizeRejectionAnswer);

export interface ReviewerOptionsPage {
  options: ReviewerOption[];
  hasMore: boolean;
}

/**
 * Loader for the searchable, paginated reviewer select. The logged user is left out: nobody gives
 * the intermediate approval to their own proposal (the backend answers REVIEWER_NOT_FOUND).
 */
export const useReviewerOptionsLoader = () => {
  const client = useApolloClient();
  const userId = useContext(ContextApp)?.user?.id;

  return useCallback(
    async (searchTerm: string, page: number): Promise<ReviewerOptionsPage> => {
      const { data } = await client.query<Query>({
        query: reviewerOptionsQuery,
        variables: { filter: buildReviewerFilter(searchTerm, page) },
        fetchPolicy: "no-cache",
        errorPolicy: "none",
      });
      const users = data?.base?.users;

      return {
        options: withoutUser(users?.items, userId).map(toReviewerOption),
        hasMore: !!users?.pageInfo?.hasNextPage,
      };
    },
    [client, userId],
  );
};

export interface ProposalsContext {
  scenarioId: string;
  originId: string;
}

export type AssignmentProposalLoadOutcome =
  | {
      kind: "loaded";
      proposal: AssignmentProposal;
      content: unknown;
      /** Of a rejected proposal: `rejectionReason` or, while it comes null, the approval history. */
      rejectionReason: string | null;
    }
  | { kind: "rejected"; errorCode: AssignmentProposalErrorCode }
  | { kind: "failed" };

/**
 * A proposal with its content, to open the assignation editor for it: one of the user's own, or
 * with `isReview` one waiting for their review.
 */
export const useAssignmentProposalLoader = () => {
  const client = useApolloClient();

  return useCallback(
    async ({
      scenarioId,
      originId,
      linkId,
      proposalId,
      isReview = false,
    }: ProposalsContext & {
      linkId: string;
      proposalId: string;
      isReview?: boolean;
    }): Promise<AssignmentProposalLoadOutcome> => {
      try {
        const { data } = await client.query<Query>({
          query: isReview ? assignmentProposalForReviewQuery : assignmentProposalQuery,
          variables: { scenarioId, originId, linkId, proposalId },
          fetchPolicy: "no-cache",
          errorPolicy: "none",
        });
        const result: AssignmentProposalResult | null | undefined = isReview
          ? data?.cube?.assignmentProposalForReview
          : data?.cube?.assignmentProposal;

        if (result?.errorCode) return { kind: "rejected", errorCode: result.errorCode };
        if (!result?.proposal || result.content == null) return { kind: "failed" };

        return {
          kind: "loaded",
          proposal: result.proposal,
          content: result.content,
          rejectionReason: isReview
            ? null
            : getRejectionReason(result.proposal.rejectionReason, result.approvalTimeline),
        };
      } catch (error) {
        console.error("[AssignmentProposal]", error);
        return { kind: "failed" };
      }
    },
    [client],
  );
};
